import HeaderClientes from "components/_refactor/headers/clients";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import { IsMobile } from "utils/is-mobile";
import { Box, Button, Typography } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { Chart } from "chart.js";
import { chartOptions, parseOptions } from "variables/charts.js";
import moment from "moment";
import Api from "lib/api/httpRequestHelper";
import LoadingData from "components/Loading/LoadingData";
import FromToDateField from "components/_refactor/fields/from-to-date";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

const useStyles = makeStyles(componentStyles);

const DONUT_COLORS = ["#122A44", "#E1B24A", "#5D657E", "#A8AEBC"];

export default function MeusResultados() {
  const classes = useStyles();
  const isMobile = IsMobile();
  const snackbar = useSnackbar();
  const lineChartRef = useRef(null);
  const donutChartRef = useRef(null);
  const lineChartInstance = useRef(null);
  const donutChartInstance = useRef(null);

  const [salesData, setSalesData] = useState(null);
  const [salesCompositionData, setSalesCompositionData] = useState(null);
  const [searching, setSearching] = useState(true);
  const [initialDate, setInitialDate] = useState(
    moment().startOf("year").format("DD/MM/YYYY")
  );
  const [finalDate, setFinalDate] = useState(
    moment(new Date()).format("DD/MM/YYYY")
  );

  useEffect(() => {
    parseOptions(Chart, chartOptions());
  }, []);

  const validatePeriod = () => {
    if (!initialDate || !finalDate) {
      return { valid: false, message: "Por favor, selecione o período inicial e final" };
    }
    const dataInicioMoment = moment(initialDate, "DD/MM/YYYY");
    const dataFimMoment = moment(finalDate, "DD/MM/YYYY");
    if (!dataInicioMoment.isValid() || !dataFimMoment.isValid()) {
      return { valid: false, message: "Por favor, selecione datas válidas" };
    }
    if (dataFimMoment.isBefore(dataInicioMoment, "day")) {
      return { valid: false, message: "A data final deve ser maior ou igual à data inicial" };
    }
    const hoje = moment().startOf("day");
    if (dataFimMoment.isAfter(hoje, "day")) {
      return { valid: false, message: "A data final não pode ser maior que hoje" };
    }
    if (dataInicioMoment.isAfter(hoje, "day")) {
      return { valid: false, message: "A data inicial não pode ser maior que hoje" };
    }
    return { valid: true };
  };

  const fetchSalesEvolution = async () => {
    const dataInicio = moment(initialDate, "DD/MM/YYYY").format("YYYY-MM-DD");
    const dataFim = moment(finalDate, "DD/MM/YYYY").format("YYYY-MM-DD");
    const parameter = `?dataInicio=${dataInicio}&dataFim=${dataFim}`;
    try {
      const response = await Api.get(
        "sales-evolution",
        "franquias",
        parameter,
        true
      );
      if (response.Success && response.Result) {
        setSalesData(response.Result);
      } else {
        setSalesData(null);
      }
    } catch (error) {
      setSalesData(null);
    }
  };

  const fetchSalesComposition = async () => {
    const dataInicio = moment(initialDate, "DD/MM/YYYY").format("YYYY-MM-DD");
    const dataFim = moment(finalDate, "DD/MM/YYYY").format("YYYY-MM-DD");
    const parameter = `?dataInicio=${dataInicio}&dataFim=${dataFim}`;
    try {
      const response = await Api.get(
        "sales-composition",
        "franquias",
        parameter,
        true
      );
      if (response.Success && response.Result) {
        setSalesCompositionData(response.Result);
      } else {
        setSalesCompositionData(null);
      }
    } catch (error) {
      setSalesCompositionData(null);
    }
  };

  const handleSearch = async () => {
    const { valid, message } = validatePeriod();
    if (!valid) {
      snackbar.showMessage(message, { severity: "warning" });
      return;
    }
    setSearching(true);
    try {
      await Promise.all([fetchSalesEvolution(), fetchSalesComposition()]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!lineChartRef.current || !donutChartRef.current) return;

    if (lineChartInstance.current) lineChartInstance.current.destroy();
    if (donutChartInstance.current) donutChartInstance.current.destroy();

    const meses = salesData?.Meses
      ? [...salesData.Meses].sort((a, b) => a.Mes.localeCompare(b.Mes))
      : [];

    const shortLabel = (mesFormat) => {
      if (!mesFormat) return "";
      const monthPart = mesFormat.split("/")[0] || "";
      return monthPart.length >= 3 ? monthPart.substring(0, 3) : monthPart;
    };

    const lineData = {
      labels: meses.map((m) => shortLabel(m.MesFormat)),
      datasets: [
        {
          label: "Faturamento Mensal (R$)",
          data: meses.map((m) => m.FaturamentoMensal || 0),
          backgroundColor: "rgba(21, 96, 130, 0.08)",
          borderColor: "#0D2741",
          pointBackgroundColor: "#0D2741",
          fill: true,
        },
      ],
    };

    const lineOptions = {
      legend: { display: true, position: "top" },
      scales: {
        yAxes: [
          {
            ticks: {
              callback: function (value) {
                return "R$ " + value.toLocaleString("pt-BR");
              },
            },
          },
        ],
      },
      tooltips: {
        callbacks: {
          label: function (item, data) {
            const label = data.datasets[item.datasetIndex].label || "";
            const value = item.yLabel;
            return `${label}: R$ ${Number(value).toLocaleString("pt-BR")}`;
          },
        },
      },
    };

    lineChartInstance.current = new Chart(lineChartRef.current.getContext("2d"), {
      type: "line",
      data: lineData,
      options: lineOptions,
    });

    const categorias = salesCompositionData?.Categorias || [];
    const hasCategorias = categorias.length > 0;
    const donutLabels = hasCategorias
      ? categorias.map((c) => c.Categoria)
      : ["Sem dados"];
    const donutValues = hasCategorias
      ? categorias.map((c) => c.ValorTotal || 0)
      : [1];

    const donutData = {
      labels: donutLabels,
      datasets: [
        {
          data: donutValues,
          backgroundColor: donutLabels.map(
            (_, i) => DONUT_COLORS[i % DONUT_COLORS.length]
          ),
          borderColor: donutLabels.map(() => "#fff"),
        },
      ],
    };

    const donutOptions = {
      legend: { display: true, position: "top" },
      cutoutPercentage: 70,
      tooltips: {
        callbacks: {
          label: function (item, data) {
            const cat = categorias[item.index];
            const pct = hasCategorias && cat ? cat.PercentualFormat : "0%";
            const valor =
              hasCategorias && cat ? ` - ${cat.ValorTotalFormat}` : "";
            return `${data.labels[item.index]}: ${pct}${valor}`;
          },
        },
      },
    };

    donutChartInstance.current = new Chart(
      donutChartRef.current.getContext("2d"),
      {
        type: "doughnut",
        data: donutData,
        options: donutOptions,
      }
    );

    return () => {
      if (lineChartInstance.current) lineChartInstance.current.destroy();
      if (donutChartInstance.current) donutChartInstance.current.destroy();
    };
  }, [salesData, salesCompositionData, searching]);

  return (
    <>
      <HeaderClientes section="Desempenho Franquias" subsection="Dashboard" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <Grid
                container
                className="pl-4"
                style={{ padding: isMobile ? "0 24px 10px" : "24px" }}
              >
                <Grid item xs={12}>
                  <Box component={Typography} variant="h2">
                    <Box component="span">Meus Resultados</Box>
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "16px",
                    }}
                  >
                    <FromToDateField
                      label="Período"
                      initialDate={initialDate}
                      finalDate={finalDate}
                      setInitialDate={setInitialDate}
                      setFinalDate={setFinalDate}
                      disabled={searching}
                      validateMaxDate={true}
                      validateDateRange={true}
                    />
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        disabled={searching}
                        style={{ marginBottom: isMobile ? "0.5rem" : "0" }}
                      >
                        Pesquisar
                      </Button>
                    </Grid>
                  </Box>
                  <Box style={{ marginTop: "16px" }}>
                    {searching ? (
                      <LoadingData
                        searching={searching}
                        label="Buscando dados..."
                      />
                    ) : (
                      <Grid container spacing={3} style={{ marginTop: "8px" }}>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="h3"
                            component={Box}
                            style={{ marginBottom: "0.5rem" }}
                            align="center"
                          >
                            Evolução do Faturamento
                          </Typography>
                          <div style={{ height: "300px", width: "100%" }}>
                            <canvas ref={lineChartRef} />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="h3"
                            component={Box}
                            style={{ marginBottom: "0.5rem" }}
                            align="center"
                          >
                            Composição de Vendas
                          </Typography>
                          <div
                            style={{
                              height: "300px",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <canvas ref={donutChartRef} />
                          </div>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
